/**
 * Transcription Service
 * Handles Deepgram token requests with caching
 */

import type { TokenRequest, TokenResponse } from '@refinevoice/shared';
import { API_ENDPOINTS, WIDGET_API_ENDPOINTS } from '@refinevoice/shared';
import { useTokenStore } from '../store/useTokenStore';
import { type ApiRequestOptions, ApiService } from './api.service';

/**
 * Transcription service for managing Deepgram tokens
 */
export class TranscriptionService extends ApiService {
  /**
   * Detect if the app is running in widget context
   * @returns True if accessed as a widget
   */
  private isWidgetContext(): boolean {
    // Check if we're in an iframe
    if (window.self !== window.top) {
      return true;
    }

    // Check referrer for widget indicators
    if (document.referrer) {
      const referrer = new URL(document.referrer);
      const referrerPath = referrer.pathname;
      // Check if referrer contains widget-related paths
      if (referrerPath.includes('/widget') || referrer.searchParams.has('widget')) {
        return true;
      }
    }

    // Check current URL for widget parameters
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('widget') || urlParams.has('widgetKey')) {
      return true;
    }

    // Check path for widget routes
    if (window.location.pathname.includes('/widget')) {
      return true;
    }

    return false;
  }
  /**
   * Get Deepgram token with automatic caching
   * Uses existing token if still valid, otherwise fetches new one
   *
   * @param options - Optional request options for timeout, retries, and cancellation
   * @returns Token response with token and expiration info
   * @throws ApiError on request failure
   *
   * @example
   * ```typescript
   * const service = new TranscriptionService();
   * const { token, expiresAt } = await service.getToken();
   * console.log(`Token valid until: ${expiresAt}`);
   * ```
   */
  async getToken(options?: ApiRequestOptions): Promise<TokenResponse> {
    // Check for cached valid token
    const cachedToken = useTokenStore.getState().getCachedToken();
    if (cachedToken) {
      // Return cached token with expiration info
      const tokenCache = useTokenStore.getState().cachedToken;
      if (tokenCache) {
        return {
          token: cachedToken,
          expiresAt: tokenCache.expiresAt,
          expiresIn: this.calculateExpiresIn(tokenCache.expiresAt),
        };
      }
    }

    // Fetch new token
    const tokenRequest: TokenRequest = {};

    // Use widget endpoint if in widget context
    const endpoint = this.isWidgetContext() ? WIDGET_API_ENDPOINTS.TOKEN : API_ENDPOINTS.TOKEN;

    const tokenResponse = await this.request<TokenResponse>(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(tokenRequest),
      ...options,
    });

    // Cache the token for future use
    useTokenStore.getState().setCachedToken(tokenResponse);

    return tokenResponse;
  }

  /**
   * Clear cached token
   * Useful when token becomes invalid or user signs out
   *
   * @example
   * ```typescript
   * const service = new TranscriptionService();
   * service.clearToken();
   * ```
   */
  clearToken(): void {
    useTokenStore.getState().clearCachedToken();
  }

  /**
   * Check if cached token is still valid
   *
   * @returns True if a valid token is cached
   *
   * @example
   * ```typescript
   * const service = new TranscriptionService();
   * if (service.hasValidToken()) {
   *   console.log('Token is ready to use');
   * }
   * ```
   */
  hasValidToken(): boolean {
    return useTokenStore.getState().isTokenValid();
  }

  /**
   * Calculate seconds until token expiration
   *
   * @param expiresAt - ISO 8601 expiration timestamp
   * @returns Seconds until expiration
   */
  private calculateExpiresIn(expiresAt: string): number {
    const expirationTime = new Date(expiresAt).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((expirationTime - now) / 1000));
  }
}
